/**
 * Based on the old cheaper three bloom, with thresholding
 */
import { Pass } from 'three/examples/jsm/postprocessing/Pass';
import {
  AdditiveBlending,
  Color,
  LinearFilter,
  NotEqualStencilFunc,
  RGBAFormat,
  ShaderMaterial,
  UniformsUtils,
  Vector2,
  WebGLRenderTarget,
} from 'three';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';
import { ConvolutionShader } from 'three/examples/jsm/shaders/ConvolutionShader';
import { LuminosityHighPassShader } from 'three/examples/jsm/shaders/LuminosityHighPassShader';

export class CustomBloomPass extends Pass {
  constructor(strength, threshold, kernelSize, sigma, resolution) {
    super();
    strength = strength !== undefined ? strength : 1;
    kernelSize = kernelSize !== undefined ? kernelSize : 25;
    sigma = sigma !== undefined ? sigma : 4.0;
    resolution = resolution !== undefined ? resolution : 512;

    // render targets

    this.threshold = threshold;

    const pars = {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      format: RGBAFormat,
    };

    this.clearColor = new Color(0, 0, 0);
    this.renderTargetX = new WebGLRenderTarget(resolution, resolution, pars);
    this.renderTargetX.texture.name = 'BloomPass.x';
    this.renderTargetY = new WebGLRenderTarget(resolution, resolution, pars);
    this.renderTargetY.texture.name = 'BloomPass.y';

    // copy material

    // eslint-disable-next-line no-console
    if (CopyShader === undefined) console.error('THREE.BloomPass relies on THREE.CopyShader');

    const copyShader = CopyShader;

    this.copyUniforms = UniformsUtils.clone(copyShader.uniforms);

    this.copyUniforms.opacity.value = strength;

    if (LuminosityHighPassShader === undefined) {
      // eslint-disable-next-line no-console
      console.error('THREE.UnrealBloomPass relies on THREE.LuminosityHighPassShader');
    }

    const highPassShader = LuminosityHighPassShader;
    this.highPassUniforms = UniformsUtils.clone(highPassShader.uniforms);

    this.highPassUniforms.luminosityThreshold.value = threshold;
    this.highPassUniforms.smoothWidth.value = 0.01;

    this.materialHighPassFilter = new ShaderMaterial({
      uniforms: this.highPassUniforms,
      vertexShader: highPassShader.vertexShader,
      fragmentShader: highPassShader.fragmentShader,
      defines: {},
      stencilRef: 50,
      stencilFunc: NotEqualStencilFunc,
    });

    this.materialCopy = new ShaderMaterial({
      uniforms: this.copyUniforms,
      vertexShader: copyShader.vertexShader,
      fragmentShader: copyShader.fragmentShader,
      blending: AdditiveBlending,
      transparent: true,
    });

    // convolution material

    if (ConvolutionShader === undefined) {
      // eslint-disable-next-line no-console
      console.error('THREE.BloomPass relies on THREE.ConvolutionShader');
    }

    const convolutionShader = ConvolutionShader;

    this.convolutionUniforms = UniformsUtils.clone(convolutionShader.uniforms);

    this.convolutionUniforms.uImageIncrement.value = blurX;
    this.convolutionUniforms.cKernel.value = ConvolutionShader.buildKernel(sigma);

    this.materialConvolution = new ShaderMaterial({
      uniforms: this.convolutionUniforms,
      vertexShader: convolutionShader.vertexShader,
      fragmentShader: convolutionShader.fragmentShader,
      defines: {
        KERNEL_SIZE_FLOAT: kernelSize.toFixed(1),
        KERNEL_SIZE_INT: kernelSize.toFixed(0),
      },
    });

    this.needsSwap = false;

    this.fsQuad = new Pass.FullScreenQuad(null);

    this.oldClearColor = new Color();
    this.oldClearAlpha = 1;
  }

  render(renderer, writeBuffer, readBuffer, deltaTime, maskActive) {
    this.oldClearColor.copy(renderer.getClearColor());
    this.oldClearAlpha = renderer.getClearAlpha();
    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    if (maskActive) renderer.state.buffers.stencil.setTest(false);

    // Render quad with blured scene into texture (convolution pass 1)
    this.fsQuad.material = this.materialHighPassFilter;
    this.highPassUniforms.tDiffuse.value = readBuffer.texture;
    this.highPassUniforms.luminosityThreshold.value = this.threshold;
    renderer.setRenderTarget(this.renderTargetY);
    renderer.setClearColor(this.clearColor);
    renderer.clear();
    this.fsQuad.render(renderer);

    this.fsQuad.material = this.materialConvolution;
    this.convolutionUniforms.tDiffuse.value = this.renderTargetY.texture;
    this.convolutionUniforms.uImageIncrement.value = blurX;

    renderer.setRenderTarget(this.renderTargetX);
    renderer.clear();
    this.fsQuad.render(renderer);

    // Render quad with blured scene into texture (convolution pass 2)

    this.convolutionUniforms.tDiffuse.value = this.renderTargetX.texture;
    this.convolutionUniforms.uImageIncrement.value = blurY;

    renderer.setRenderTarget(this.renderTargetY);
    renderer.clear();
    this.fsQuad.render(renderer);

    // Render original scene with superimposed blur to texture

    this.fsQuad.material = this.materialCopy;

    this.copyUniforms.tDiffuse.value = this.renderTargetY.texture;

    if (maskActive) renderer.state.buffers.stencil.setTest(true);

    renderer.setRenderTarget(readBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);

    renderer.setClearColor(this.oldClearColor, this.oldClearAlpha);
    renderer.autoClear = oldAutoClear;
  }
}

const blurX = new Vector2(0.001953125, 0.0);
const blurY = new Vector2(0.0, 0.001953125);
